import React, { useCallback, useMemo } from "react";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import { useToast } from "@src/lib/hooks/useToast";
import { useAIAssistBrandSelector } from "@src/features/aiAssist/hooks/useAIAssistBrandSelector";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";

import { useStartGenModuleFromOutlineChatWorkflowMutation } from "@src/features/assistantChat/aiChat.generated";
import useTrackClientFacingWorkflow from "@src/features/app/hooks/useTrackClientFacingWorkflow";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import ChatCanvasDialogContainer from "./ChatCanvasDialogContainer";
import { AiChatType, Ff } from "@src/types.generated";
import {
  MODULE_OUTLINE_TITLE,
  MODULE_OUTLINE_DESCRIPTION,
} from "./NewModuleDialog";

type Props = {
  dialogIsOpen: boolean;
  setDialogIsopen: (open: boolean) => void;
  onModuleCreated: (libraryItemId: string) => void;
};

const ModuleOutlineCanvasDialog: React.FC<Props> = ({
  dialogIsOpen,
  setDialogIsopen,
  onModuleCreated,
}) => {
  const { aiSessionId, submitMessage } = useChatContext();
  const { selectedSession } = useChatSidebar(AiChatType.ModuleOutline);
  const { addErrorToast } = useToast();
  // With product-container generation, the module shell is created quickly and
  // we redirect into the builder, where per-item progress + the "x/y completed"
  // banner show real status. So the modal's simulated percentage bar is
  // redundant; suppress it behind the same flag.
  const productContainerGenEnabled = useFeatureFlag(
    Ff.ProductContainerCourseGeneration,
  );

  const { trackClientFacingWorkflow, isTracking } =
    useTrackClientFacingWorkflow({
      onCompleted: (cfw) => {
        if (cfw.result?.libraryItem) {
          onModuleCreated(cfw.result.libraryItem.id);
          setDialogIsopen(false);
        }
      },
      onError: () => {
        addErrorToast({
          callsite: "module_generation_workflow_failed",
        });
      },
    });

  const [startModuleGenerationWorkflow] =
    useStartGenModuleFromOutlineChatWorkflowMutation({
      onCompleted: (data) => {
        if (
          data.startGenModuleFromOutlineChatWorkflow.success &&
          data.startGenModuleFromOutlineChatWorkflow.clientFacingWorkflow
        ) {
          trackClientFacingWorkflow(
            data.startGenModuleFromOutlineChatWorkflow.clientFacingWorkflow,
          );
        } else {
          addErrorToast({
            callsite: "start_module_generation_workflow_from_ai_chat",
          });
        }
      },
      onError: () => {
        addErrorToast({
          callsite: "start_module_generation_workflow_from_ai_chat",
        });
      },
    });
  const brandSelectorProps = useAIAssistBrandSelector();
  const generateModule = useCallback(() => {
    startModuleGenerationWorkflow({
      variables: {
        input: {
          aiChatId: aiSessionId,
          moduleOutlineText: selectedSession?.artifact || "",
          brandDetailsId: brandSelectorProps.selectedBrandItem?.brandDetailsId,
        },
      },
    });
  }, [
    startModuleGenerationWorkflow,
    aiSessionId,
    selectedSession?.artifact,
    brandSelectorProps.selectedBrandItem?.brandDetailsId,
  ]);

  const threadActionButtonHandlers = useMemo<Record<string, () => void>>(() => {
    const handlers: Record<string, () => void> = {};
    // With product-container generation, Generate is a persistent button at the
    // top of the modal (matching the course outline modal), so we do not surface
    // the agent's "Add Details" / "Generate Module" thread buttons.
    if (!productContainerGenEnabled) {
      handlers["Add Details"] = () => {
        submitMessage({
          role: "user",
          content:
            "Fill in the outline with all of the remaining details needed to generate the module.",
          customMessageData: {
            attachments: [{ canvasContent: selectedSession?.artifact }],
          },
        });
      };
      handlers["Generate Module"] = generateModule;
    }
    return handlers;
  }, [
    productContainerGenEnabled,
    generateModule,
    submitMessage,
    selectedSession?.artifact,
  ]);
  return (
    <ChatCanvasDialogContainer
      open={dialogIsOpen}
      chatType={AiChatType.ModuleOutline}
      setDialogIsOpen={setDialogIsopen}
      threadActionButtonHandlers={threadActionButtonHandlers}
      actionLabel={productContainerGenEnabled ? "Generate module" : undefined}
      actionOnClick={productContainerGenEnabled ? generateModule : undefined}
      actionLoading={productContainerGenEnabled ? isTracking : undefined}
      canvasSubtitle="Module outline canvas"
      threadEmptyStateTitle={MODULE_OUTLINE_TITLE}
      threadEmptyStateSubtitle={MODULE_OUTLINE_DESCRIPTION}
      composerPlaceholder="Describe your module..."
      brandSelectorProps={brandSelectorProps}
      showConversionProgress={!productContainerGenEnabled && isTracking}
      chatSidebarTitle="Opus AI"
    />
  );
};

export default ModuleOutlineCanvasDialog;
